import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getDashboardStats, getNotifications, uploadDocument, getCompanies, getTasks, createTask, updateTaskStatus, deleteTask, getDocuments } from '../services/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Rocket, Calendar as CalendarIcon, Bell, Upload, Plus, 
  Clock, Sun, Moon, Cloud, CloudRain, Thermometer,
  LayoutGrid, Settings, LogOut, Building2, ChevronDown,
  FolderOpen, Menu, X, FileText, Circle, CheckCircle2,
  Wallet, Flame, ArrowRight, Zap, Flag, Check,
  Search, Trash2, CloudSun, Droplets, CreditCard,
  ChevronLeft, ChevronRight, Users, AlertCircle, ShieldAlert, Swords
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import Sidebar from '../components/layout/Sidebar';
import FileSyncWidget from '../components/FileSyncWidget';
import { fileSyncService } from '../services/fileSyncService';

const DashboardPage = () => {
  const { user, logout, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', due_date: '', due_time: '', priority: 'medium' });
  const [quickNote, setQuickNote] = useState('');
  const [quickInputFocused, setQuickInputFocused] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const dropZoneRef = useRef(null);
  const quickInputRef = useRef(null);

  // Simulated weather data (in production, use a weather API)
  const [weather] = useState({
    temp: 18,
    condition: 'sunny',
    humidity: 45,
    location: 'Berlin'
  });

  const loadData = useCallback(async () => {
    try {
      const [statsRes, notifRes, companiesRes, tasksRes, docsRes] = await Promise.all([
        getDashboardStats(),
        getNotifications(true),
        getCompanies(),
        getTasks({}),
        getDocuments({})
      ]);
      setStats(statsRes.data);
      setNotifications(notifRes.data);
      setCompanies(companiesRes.data);
      setTasks(tasksRes.data);
      setDocuments(docsRes.data || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    
    const params = new URLSearchParams(location.search);
    if (params.get('payment') === 'success') {
      toast.success('Zahlung erfolgreich!');
      navigate('/dashboard', { replace: true });
    } else if (params.get('payment') === 'cancelled') {
      toast.info('Zahlung abgebrochen');
      navigate('/dashboard', { replace: true });
    }
  }, [loadData, location, navigate]);

  // Auto-sync to local folder after upload
  const syncToLocalFolder = useCallback(async () => {
    const status = fileSyncService.getStatus();
    if (status.hasDirectory && token) {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        await fileSyncService.syncFromServer(token, API_URL);
      } catch (error) {
        console.log('Auto-sync info:', error.message);
      }
    }
  }, [token]);

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    for (const file of files) {
      setUploading(true);
      try {
        await uploadDocument(file, selectedCompany?.id, false);
        toast.success(`${file.name} hochgeladen!`);
      } catch (error) {
        toast.error(`Fehler bei ${file.name}`);
      }
    }
    setUploading(false);
    loadData();
    // Auto-sync to local folder
    syncToLocalFolder();
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const file of files) {
      setUploading(true);
      try {
        await uploadDocument(file, selectedCompany?.id, false);
        toast.success(`${file.name} hochgeladen!`);
      } catch (error) {
        toast.error('Fehler beim Hochladen');
      }
    }
    setUploading(false);
    loadData();
    // Auto-sync to local folder
    syncToLocalFolder();
  };

  const handleQuickAddTask = async () => {
    if (!newTask.title.trim()) {
      toast.error('Bitte Aufgabe eingeben');
      return;
    }
    try {
      const taskData = {
        title: newTask.title,
        priority: newTask.priority,
        due_date: newTask.due_date ? (newTask.due_time ? `${newTask.due_date}T${newTask.due_time}` : newTask.due_date) : ''
      };
      await createTask(taskData);
      toast.success('Aufgabe erstellt!');
      setNewTask({ title: '', due_date: '', due_time: '', priority: 'medium' });
      setQuickAddOpen(false);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleToggleTask = async (task) => {
    const newStatus = task.status === 'done' ? 'open' : 'done';
    setTasks(tasks.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    try {
      await updateTaskStatus(task.id, newStatus);
    } catch (error) {
      setTasks(tasks.map(t => t.id === task.id ? { ...t, status: task.status } : t));
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask(taskId);
      setTasks(tasks.filter(t => t.id !== taskId));
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  // Calculate days until deadline
  const getDaysUntilDeadline = (dueDate) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get urgency badge based on days
  const getDeadlineBadge = (days) => {
    if (days === null) return { text: 'Keine Frist', color: 'gray' };
    if (days < 0) return { text: `${Math.abs(days)} Tage überfällig!`, color: 'red', urgent: true };
    if (days === 0) return { text: 'Heute fällig!', color: 'red', urgent: true };
    if (days === 1) return { text: 'Morgen fällig!', color: 'orange', urgent: true };
    if (days <= 3) return { text: `Noch ${days} Tage`, color: 'orange' };
    if (days <= 7) return { text: `Noch ${days} Tage`, color: 'amber' };
    if (days <= 14) return { text: `Noch ${days} Tage`, color: 'yellow' };
    return { text: `Noch ${days} Tage`, color: 'green' };
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Guten Morgen';
    if (hour < 18) return 'Guten Tag';
    return 'Guten Abend';
  };

  const getWeatherIcon = () => {
    switch (weather.condition) {
      case 'sunny': return Sun;
      case 'cloudy': return Cloud;
      case 'partly-cloudy': return CloudSun;
      case 'rainy': return CloudRain;
      default: return Sun;
    }
  };

  const WeatherIcon = getWeatherIcon();
  const todayTasks = tasks.filter(t => t.status !== 'done').slice(0, 5);
  const completedToday = tasks.filter(t => t.status === 'done').length;
  
  // Urgent documents and upcoming deadlines for Frist-Wächter
  const urgentDocs = documents.filter(d => d.status === 'urgent').slice(0, 4);
  const upcomingDeadlines = documents
    .filter(d => d.due_date && d.status !== 'done')
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    .slice(0, 4);
  const recentUploads = [...documents]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 3);
  
  // Mini Calendar helpers
  const monthStart = startOfMonth(calendarDate);
  const monthEnd = endOfMonth(calendarDate);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const firstDayOfMonth = monthStart.getDay();
  const paddingDays = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen relative overflow-hidden ${theme === 'dark' ? 'galaxy-bg' : 'bg-background'}`} data-testid="dashboard-page">
      {/* Light Mode Background Blobs */}
      {theme === 'light' && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-1/2 -left-40 w-96 h-96 bg-violet-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-40 right-1/3 w-96 h-96 bg-pink-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>
      )}

      <div className="flex relative z-10">
        <Sidebar />

        <main className="flex-1 min-h-screen p-6 lg:p-8">
          {/* Top Bar - Weather, Greeting & Quick Shortcuts */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between mb-8"
          >
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {getGreeting()}, {user?.name?.split(' ')[0] || 'User'}
              </h1>
              <p className="text-muted-foreground mt-1">
                {format(new Date(), 'EEEE, d. MMMM yyyy', { locale: de })}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Quick Shortcuts */}
              <div className="hidden md:flex items-center gap-2 mr-4">
                <Link to="/board">
                  <Button variant="ghost" size="icon" className="w-10 h-10 rounded-xl glass-card dark:bg-white/5 hover:bg-violet-500/10">
                    <LayoutGrid className="w-5 h-5 text-violet-500" />
                  </Button>
                </Link>
                <Link to="/calendar">
                  <Button variant="ghost" size="icon" className="w-10 h-10 rounded-xl glass-card dark:bg-white/5 hover:bg-blue-500/10">
                    <CalendarIcon className="w-5 h-5 text-blue-500" />
                  </Button>
                </Link>
                <Link to="/archive">
                  <Button variant="ghost" size="icon" className="w-10 h-10 rounded-xl glass-card dark:bg-white/5 hover:bg-emerald-500/10">
                    <FolderOpen className="w-5 h-5 text-emerald-500" />
                  </Button>
                </Link>
                <Link to="/contacts">
                  <Button variant="ghost" size="icon" className="w-10 h-10 rounded-xl glass-card dark:bg-white/5 hover:bg-pink-500/10">
                    <Users className="w-5 h-5 text-pink-500" />
                  </Button>
                </Link>
              </div>

              {/* Weather Widget - Galaxy Style */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="glass-card rounded-2xl px-6 py-4 flex items-center gap-4 dark:bg-white/5 dark:border-white/10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
                    <WeatherIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{weather.temp}°</p>
                    <p className="text-sm text-muted-foreground">{weather.location}</p>
                  </div>
                </div>
                <div className="h-10 w-px bg-white/10"></div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Droplets className="w-4 h-4" />
                  <span className="text-sm">{weather.humidity}%</span>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Main Grid Layout */}
          <div className="grid grid-cols-12 gap-6">
            {/* Left Column - Stats & Documents */}
            <div className="col-span-12 lg:col-span-8 space-y-6">
              {/* Stats Cards - Galaxy Style */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="grid grid-cols-2 lg:grid-cols-4 gap-4"
              >
                {/* Total Open */}
                <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-2xl p-5 shadow-xl shadow-violet-500/20 hover:scale-[1.02] transition-transform">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(stats?.total_open_amount)}</p>
                  <p className="text-sm text-white/70">Offene Summe</p>
                </div>

                {/* Urgent */}
                <div className="glass-card rounded-2xl p-5 group hover:scale-[1.02] transition-transform dark:bg-white/5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
                      <Flame className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-orange-500">{stats?.urgent_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Dringend</p>
                </div>

                {/* In Progress */}
                <div className="glass-card rounded-2xl p-5 group hover:scale-[1.02] transition-transform dark:bg-white/5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-blue-500">{stats?.progress_count || 0}</p>
                  <p className="text-sm text-muted-foreground">In der Pipeline</p>
                </div>

                {/* Completed */}
                <div className="glass-card rounded-2xl p-5 group hover:scale-[1.02] transition-transform dark:bg-white/5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-emerald-500">{stats?.done_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Erledigt</p>
                </div>
              </motion.div>

              {/* Upload Zone - Galaxy Style */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                onDrop={handleDrop}
                className={`glass-card rounded-2xl p-8 border-2 border-dashed transition-all dark:bg-white/5 ${
                  isDragging 
                    ? 'border-violet-500 bg-violet-500/10 dark:bg-violet-500/20' 
                    : 'border-white/20 dark:border-white/10 hover:border-violet-500/50'
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 transition-all ${
                    isDragging 
                      ? 'bg-violet-500 scale-110' 
                      : 'bg-gradient-to-br from-violet-500/20 to-purple-500/20 dark:from-violet-500/30 dark:to-purple-500/30'
                  }`}>
                    <Upload className={`w-8 h-8 ${isDragging ? 'text-white' : 'text-violet-500'}`} />
                  </div>
                  <p className="text-lg font-semibold text-foreground mb-1">
                    {uploading ? 'Wird hochgeladen...' : 'Dokument hochladen'}
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    PDF, JPG oder PNG per Drag & Drop
                  </p>
                  <label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.txt"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      className="rounded-xl glass-input cursor-pointer dark:bg-white/10 dark:border-white/20 dark:hover:bg-white/20"
                      disabled={uploading}
                    >
                      Datei auswählen
                    </Button>
                  </label>
                </div>
              </motion.div>

              {/* FRIST-WÄCHTER - Improved with Days Counter and Shield Icon */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-4"
              >
                {/* Frist-Wächter Header */}
                <div className="glass-card rounded-2xl overflow-hidden dark:bg-white/5">
                  <div className="px-5 py-4 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <ShieldAlert className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg flex items-center gap-2">
                          Frist-Wächter
                          <Swords className="w-5 h-5 text-white/80" />
                        </h3>
                        <p className="text-white/70 text-xs">{urgentDocs.length + upcomingDeadlines.length} Dokumente mit Fristen</p>
                      </div>
                    </div>
                    <Link to="/board">
                      <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 rounded-lg">
                        Alle
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                  
                  <div className="p-4">
                    {urgentDocs.length === 0 && upcomingDeadlines.length === 0 ? (
                      <div className="text-center py-6">
                        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                        <p className="text-foreground font-medium">Alles im grünen Bereich!</p>
                        <p className="text-sm text-muted-foreground">Keine dringenden Fristen</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* All documents with deadlines, sorted by urgency */}
                        {[...urgentDocs, ...upcomingDeadlines.filter(d => d.status !== 'urgent')]
                          .slice(0, 5)
                          .map((doc, index) => {
                            const days = getDaysUntilDeadline(doc.due_date);
                            const badge = getDeadlineBadge(days);
                            const isUrgent = badge.urgent || doc.status === 'urgent';
                            
                            return (
                              <motion.div
                                key={doc.id}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className={`flex items-center gap-4 p-3 rounded-xl cursor-pointer transition-all ${
                                  isUrgent 
                                    ? 'bg-red-500/10 border border-red-500/30 hover:bg-red-500/20' 
                                    : badge.color === 'orange' || badge.color === 'amber'
                                      ? 'bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20'
                                      : 'bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20'
                                }`}
                                onClick={() => navigate('/board')}
                              >
                                {/* Days Badge - Prominent */}
                                <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${
                                  isUrgent ? 'bg-red-500' : 
                                  badge.color === 'orange' || badge.color === 'amber' ? 'bg-amber-500' : 
                                  'bg-emerald-500'
                                }`}>
                                  {days !== null ? (
                                    <>
                                      <span className="text-white font-bold text-xl leading-none">
                                        {days < 0 ? Math.abs(days) : days}
                                      </span>
                                      <span className="text-white/80 text-[10px] font-medium">
                                        {days < 0 ? 'ÜBER' : days === 1 ? 'TAG' : 'TAGE'}
                                      </span>
                                    </>
                                  ) : (
                                    <Clock className="w-6 h-6 text-white" />
                                  )}
                                </div>
                                
                                {/* Document Info */}
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-foreground truncate">{doc.sender_name || 'Unbekannt'}</p>
                                  <p className={`text-xs font-medium ${
                                    isUrgent ? 'text-red-400' : 
                                    badge.color === 'orange' || badge.color === 'amber' ? 'text-amber-400' : 
                                    'text-emerald-400'
                                  }`}>
                                    {badge.text}
                                    {doc.due_date && ` · ${format(new Date(doc.due_date), 'd. MMM', { locale: de })}`}
                                  </p>
                                </div>
                                
                                {/* Amount and Type */}
                                <div className="text-right">
                                  <p className="font-bold text-foreground text-lg">{formatCurrency(doc.amount)}</p>
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    isUrgent ? 'bg-red-500/20 text-red-400' : 
                                    badge.color === 'orange' || badge.color === 'amber' ? 'bg-amber-500/20 text-amber-400' : 
                                    'bg-emerald-500/20 text-emerald-400'
                                  }`}>
                                    {doc.doc_type || 'Dokument'}
                                  </span>
                                </div>
                              </motion.div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Tasks First, then Collapsible Calendar */}
            <div className="col-span-12 lg:col-span-4">
              <div className="lg:sticky lg:top-6 space-y-6">
              
              {/* File Sync Widget */}
              <FileSyncWidget onDocumentUploaded={loadData} />
              
              {/* Tasks To-Do - Now FIRST */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="glass-card rounded-3xl overflow-hidden dark:bg-white/5 dark:border-white/10">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-bold text-lg">Meine Aufgaben</h3>
                        <p className="text-white/70 text-sm">{todayTasks.filter(t => t.status !== 'done').length} offen</p>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-white/20 hover:bg-white/30 text-white rounded-xl"
                        onClick={() => setQuickAddOpen(true)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Neu
                      </Button>
                    </div>
                  </div>

                  {/* Quick Add - With clickable Zap icon */}
                  <div className="p-4 border-b border-white/10 dark:border-white/5">
                    <div 
                      className={`flex items-center gap-3 rounded-xl px-4 py-3 transition-all cursor-text ${
                        quickInputFocused 
                          ? 'bg-violet-500/20 ring-2 ring-violet-500/50' 
                          : 'bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10'
                      }`}
                      onClick={() => {
                        setQuickInputFocused(true);
                        quickInputRef.current?.focus();
                      }}
                    >
                      <motion.div 
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuickInputFocused(true);
                          quickInputRef.current?.focus();
                        }}
                      >
                        <Zap className={`w-5 h-5 transition-colors ${quickInputFocused ? 'text-violet-400' : 'text-violet-500'}`} />
                      </motion.div>
                      <Input
                        ref={quickInputRef}
                        value={quickNote}
                        onChange={(e) => setQuickNote(e.target.value)}
                        onFocus={() => setQuickInputFocused(true)}
                        onBlur={() => setQuickInputFocused(false)}
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && quickNote.trim()) {
                            try {
                              await createTask({ title: quickNote, priority: 'medium' });
                              setQuickNote('');
                              loadData();
                              toast.success('Aufgabe erstellt');
                            } catch (error) {
                              toast.error('Fehler');
                            }
                          }
                        }}
                        placeholder="Schnell Aufgabe hinzufügen... (Enter drücken)"
                        className="flex-1 bg-transparent border-0 focus:ring-0 h-8 text-foreground placeholder:text-muted-foreground"
                      />
                      {quickNote.trim() && (
                        <motion.button
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (quickNote.trim()) {
                              try {
                                await createTask({ title: quickNote, priority: 'medium' });
                                setQuickNote('');
                                loadData();
                                toast.success('Aufgabe erstellt');
                              } catch (error) {
                                toast.error('Fehler');
                              }
                            }
                          }}
                          className="w-8 h-8 bg-violet-500 rounded-lg flex items-center justify-center hover:bg-violet-600 transition-colors"
                        >
                          <Plus className="w-4 h-4 text-white" />
                        </motion.button>
                      )}
                    </div>
                  </div>

                  {/* Tasks List - Clean Design */}
                  <div className="p-4 space-y-2 max-h-[300px] overflow-y-auto">
                    <AnimatePresence>
                      {todayTasks.length === 0 ? (
                        <div className="text-center py-8">
                          <div className="w-12 h-12 bg-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                            <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                          </div>
                          <p className="text-foreground font-medium">Alles erledigt!</p>
                          <p className="text-muted-foreground text-sm">Keine offenen Aufgaben</p>
                        </div>
                      ) : (
                        todayTasks.map((task, index) => (
                          <motion.div
                            key={task.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ delay: index * 0.03 }}
                            className={`flex items-center gap-3 p-3 rounded-xl transition-all group cursor-pointer ${
                              task.status === 'done' 
                                ? 'bg-emerald-500/10 dark:bg-emerald-500/5' 
                                : 'bg-white/50 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10'
                            }`}
                            onClick={() => handleToggleTask(task)}
                          >
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                              task.status === 'done'
                                ? 'bg-emerald-500 border-emerald-500'
                                : task.priority === 'high' 
                                  ? 'border-red-400 group-hover:border-red-500'
                                  : 'border-gray-300 dark:border-gray-600 group-hover:border-violet-500'
                            }`}>
                              {task.status === 'done' && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm truncate ${
                                task.status === 'done' ? 'text-muted-foreground line-through' : 'text-foreground'
                              }`}>
                                {task.title}
                              </p>
                            </div>
                            {task.priority === 'high' && task.status !== 'done' && (
                              <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-500 rounded font-medium">
                                !
                              </span>
                            )}
                          </motion.div>
                        ))
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>

              {/* Mini Calendar - NOW COLLAPSIBLE */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-card rounded-2xl overflow-hidden dark:bg-white/5"
              >
                {/* Calendar Header - Clickable to expand/collapse */}
                <div 
                  className="px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-between cursor-pointer hover:from-blue-600 hover:to-cyan-600 transition-all"
                  onClick={() => setCalendarExpanded(!calendarExpanded)}
                >
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-white" />
                    <h3 className="font-semibold text-white">
                      {format(calendarDate, 'MMMM yyyy', { locale: de })}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {calendarExpanded && (
                      <>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="w-7 h-7 text-white hover:bg-white/20"
                          onClick={(e) => { e.stopPropagation(); setCalendarDate(new Date(calendarDate.setMonth(calendarDate.getMonth() - 1))); }}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="w-7 h-7 text-white hover:bg-white/20"
                          onClick={(e) => { e.stopPropagation(); setCalendarDate(new Date(calendarDate.setMonth(calendarDate.getMonth() + 1))); }}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <motion.div
                      animate={{ rotate: calendarExpanded ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-5 h-5 text-white" />
                    </motion.div>
                  </div>
                </div>
                
                {/* Calendar Body - Collapsible */}
                <AnimatePresence>
                  {calendarExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-3">
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                            <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                              {day}
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {[...Array(paddingDays)].map((_, i) => (
                            <div key={`pad-${i}`} className="aspect-square" />
                          ))}
                          {calendarDays.map(day => {
                            const hasTask = tasks.some(t => t.due_date && isSameDay(new Date(t.due_date), day));
                            const hasDoc = documents.some(d => d.due_date && isSameDay(new Date(d.due_date), day));
                            return (
                              <div
                                key={day.toISOString()}
                                className={`aspect-square flex items-center justify-center text-xs rounded-lg cursor-pointer transition-all relative ${
                                  isToday(day) 
                                    ? 'bg-violet-500 text-white font-bold' 
                                    : isSameMonth(day, calendarDate)
                                      ? 'text-foreground hover:bg-white/50 dark:hover:bg-white/10'
                                      : 'text-muted-foreground/50'
                                }`}
                                onClick={() => navigate('/calendar')}
                              >
                                {format(day, 'd')}
                                {(hasTask || hasDoc) && !isToday(day) && (
                                  <span className={`absolute bottom-0.5 w-1 h-1 rounded-full ${hasDoc ? 'bg-red-500' : 'bg-violet-500'}`} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Collapsed state - show today's date */}
                {!calendarExpanded && (
                  <div className="px-4 py-3 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Heute: {format(new Date(), 'd. MMMM', { locale: de })}</span>
                    <span className="text-muted-foreground text-xs">Klicken zum Öffnen</span>
                  </div>
                )}
              </motion.div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Quick Add Task Dialog - IMPROVED */}
      <Dialog open={quickAddOpen} onOpenChange={setQuickAddOpen}>
        <DialogContent className="dark:bg-gray-900/95 bg-white border-white/20 dark:border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Neue Aufgabe
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-4">
            {/* Titel Input - FIX: Sichtbarer Text */}
            <div>
              <Label className="text-foreground font-medium">Titel *</Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Was muss erledigt werden?"
                className="mt-2 h-12 bg-white dark:bg-white/10 border-gray-200 dark:border-white/20 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-xl text-base"
                data-testid="new-task-title-input"
              />
            </div>

            {/* Datum und Uhrzeit - Mit schönem Kalender-Popover */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-foreground font-medium flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-blue-500" />
                  Fällig am
                </Label>
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="mt-2 w-full h-12 justify-start text-left font-normal bg-white dark:bg-white/10 border-gray-200 dark:border-white/20 text-gray-900 dark:text-white rounded-xl hover:bg-gray-50 dark:hover:bg-white/20"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-blue-500" />
                      {newTask.due_date ? (
                        format(new Date(newTask.due_date), 'd. MMMM yyyy', { locale: de })
                      ) : (
                        <span className="text-muted-foreground">Datum wählen</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 dark:bg-gray-900 border-white/20 z-50" align="start" side="top" sideOffset={8}>
                    <Calendar
                      mode="single"
                      selected={newTask.due_date ? new Date(newTask.due_date) : undefined}
                      onSelect={(date) => {
                        if (date) {
                          setNewTask({ ...newTask, due_date: format(date, 'yyyy-MM-dd') });
                        }
                        setDatePickerOpen(false);
                      }}
                      locale={de}
                      initialFocus
                      className="rounded-xl"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-foreground font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4 text-cyan-500" />
                  Uhrzeit
                </Label>
                <Input
                  type="time"
                  value={newTask.due_time}
                  onChange={(e) => setNewTask({ ...newTask, due_time: e.target.value })}
                  className="mt-2 h-12 bg-white dark:bg-white/10 border-gray-200 dark:border-white/20 text-gray-900 dark:text-white rounded-xl [color-scheme:dark]"
                />
              </div>
            </div>

            {/* Priorität - Schöner mit Icons */}
            <div>
              <Label className="text-foreground font-medium flex items-center gap-2">
                <Flag className="w-4 h-4 text-amber-500" />
                Priorität
              </Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[
                  { value: 'high', label: 'Dringend', color: 'red', icon: Zap },
                  { value: 'medium', label: 'Normal', color: 'amber', icon: Flag },
                  { value: 'low', label: 'Niedrig', color: 'blue', icon: Circle }
                ].map(({ value, label, color, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setNewTask({ ...newTask, priority: value })}
                    className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                      newTask.priority === value
                        ? color === 'red' 
                          ? 'border-red-500 bg-red-500/20 text-red-500'
                          : color === 'amber'
                            ? 'border-amber-500 bg-amber-500/20 text-amber-500'
                            : 'border-blue-500 bg-blue-500/20 text-blue-500'
                        : 'border-white/20 dark:border-white/10 text-muted-foreground hover:border-white/40 dark:hover:border-white/20'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            <Button 
              onClick={handleQuickAddTask}
              className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 rounded-xl shadow-lg shadow-violet-500/25 text-white font-semibold"
              data-testid="new-task-submit-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Aufgabe erstellen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
